import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SmoothScroll } from "@/components/SmoothScroll";
import { NewUpdatesPopup } from "@/components/NewUpdatesPopup";
import { SmsmaWidget } from "@/components/SmsmaWidget";
import { Seo } from "@/components/Seo";
import { trackPage, flushDuration } from "@/lib/analytics";
import { orgSchema, stripLocale } from "@/lib/seo";
import { useLang } from "@/context/LanguageContext";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Packages from "@/pages/Packages";
import TaxUpdates from "@/pages/TaxUpdates";
import AccountingSoftware from "@/pages/AccountingSoftware";
import ServiceLanding from "@/pages/ServiceLanding";
import KnowledgeCenter from "@/pages/KnowledgeCenter";
import KnowledgeArticle from "@/pages/KnowledgeArticle";
import Admin from "@/pages/Admin";
import Vetasphere from "@/pages/Vetasphere";
import { OccasionProvider, OccasionMount } from "@/occasion/OccasionProvider";

const STATIC_SEO = {
  "/": { title: { en: "Smart Account Vision Tax Consultant | UAE Tax, Accounting & Auditing", ar: "سمارت أكاونت فيجن | ضرائب ومحاسبة وتدقيق في الإمارات" }, description: { en: "Approved Tax Agent (TAAN 20062931). VAT, Corporate Tax, accounting, bookkeeping, auditing & advisory across the UAE.", ar: "وكيل ضريبي معتمد (TAAN 20062931). ضريبة قيمة مضافة وضريبة شركات ومحاسبة وتدقيق في الإمارات." }, schema: orgSchema },
  "/services": { title: { en: "Accounting & Tax Services in the UAE | Smart Account Vision", ar: "خدمات المحاسبة والضرائب في الإمارات" }, description: { en: "Full accounting, VAT, Corporate Tax, payroll, reporting and advisory services across the UAE.", ar: "خدمات محاسبة وضرائب ورواتب وتقارير واستشارات في الإمارات." } },
  "/packages": { title: { en: "Accounting Packages & Pricing | Smart Account Vision", ar: "باقات وأسعار المحاسبة | سمارت أكاونت فيجن" }, description: { en: "Transparent accounting packages plus a live Build-Your-Own pricing calculator. Every service is calculable.", ar: "باقات محاسبة شفافة مع حاسبة أسعار تفاعلية." } },
  "/tax-updates": { title: { en: "UAE Tax Updates & FTA News | Smart Account Vision", ar: "التحديثات الضريبية وأخبار الهيئة" }, description: { en: "Latest UAE tax legislation and FTA updates from official sources.", ar: "أحدث التشريعات الضريبية وتحديثات الهيئة من مصادر رسمية." } },
  "/accounting-software": { title: { en: "Accounting Software | Smart Account Vision", ar: "برنامج المحاسبة | سمارت أكاونت فيجن" }, description: { en: "The SAV accounting system for UAE businesses.", ar: "نظام SAV المحاسبي للأعمال في الإمارات." } },
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { setTimeout(() => { lenis ? lenis.scrollTo(el, { offset: -90 }) : el.scrollIntoView({ behavior: "smooth" }); }, 80); return; }
    }
    lenis ? lenis.scrollTo(0, { immediate: true }) : window.scrollTo({ top: 0 });
  }, [pathname, hash]);
  return null;
};

const Analytics = () => {
  const { pathname } = useLocation();
  useEffect(() => { if (!pathname.startsWith("/admin")) trackPage(pathname); }, [pathname]);
  useEffect(() => {
    const onHide = () => flushDuration();
    const onVis = () => { if (document.visibilityState === "hidden") flushDuration(); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("pagehide", onHide); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  return null;
};

const LocaleLayout = () => {
  const { lang: urlLang } = useParams();
  const { setLang } = useLang();
  const { pathname } = useLocation();
  useEffect(() => { if (urlLang === "en" || urlLang === "ar") setLang(urlLang); }, [urlLang, setLang]);
  if (urlLang !== "en" && urlLang !== "ar") return <Navigate to="/en" replace />;
  const base = stripLocale(pathname);
  const s = STATIC_SEO[base];
  return (
    <SmoothScroll>
      <ScrollManager />
      {s && <Seo title={s.title} description={s.description} schema={s.schema} />}
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <WhatsAppFloat />
      <SmsmaWidget />
      <NewUpdatesPopup />
    </SmoothScroll>
  );
};

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <OccasionProvider>
      <BrowserRouter>
        <Analytics />
        <OccasionMount />
        <Routes>
          <Route path="/" element={<Navigate to="/en" replace />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/vetasphere" element={<Vetasphere />} />
          <Route path="/:lang" element={<LocaleLayout />}>
            <Route index element={<Home />} />
            <Route path="services" element={<Services />} />
            <Route path="services/:slug" element={<ServiceLanding />} />
            <Route path="packages" element={<Packages />} />
            <Route path="tax-updates" element={<TaxUpdates />} />
            <Route path="knowledge" element={<KnowledgeCenter />} />
            <Route path="knowledge/:slug" element={<KnowledgeArticle />} />
            <Route path="accounting-software" element={<AccountingSoftware />} />
          </Route>
          {/* legacy unprefixed URLs -> default locale */}
          <Route path="/services" element={<Navigate to="/en/services" replace />} />
          <Route path="/packages" element={<Navigate to="/en/packages" replace />} />
          <Route path="/tax-updates" element={<Navigate to="/en/tax-updates" replace />} />
          <Route path="/accounting-software" element={<Navigate to="/en/accounting-software" replace />} />
          <Route path="*" element={<Navigate to="/en" replace />} />
        </Routes>
      </BrowserRouter>
      </OccasionProvider>
    </div>
  );
}
export default App;
